import React, { FC, useCallback, useState } from "react";
import { Field } from "react-final-form";
import { PasswordRequirements } from "./PasswordRequirements";
import { EyeClosedIconSvg, EyeOpenIconSvg } from "../../icons";
import { FieldProps } from "../../../types/field";
import {
  StyledError,
  StyledField,
  StyledInput,
  StyledLabel,
} from "../base.styled";
import {
  StyledEyeIconContainer,
  StyledPasswordContainer,
  StyledPasswordRequirementsContainer,
} from "./index.styled";
import { asyncFieldLevelMemo } from "../../../helpers/validation";
import {
  FormsValidationLocalization,
  PortalPasswordLocalization,
} from "../../../types/graphql/generated";

// copied from v2 schema (does not currently exist in v3)
export type PasswordRequirement = {
  __typename?: "PasswordRequirement";
  msg: string;
  satisfied: boolean;
};

interface PasswordInputProps extends FieldProps {
  defaultValue?: string;
  handlePasswordChange: (password: string) => Promise<PasswordRequirement[]>;
  placeholder?: string;
  localization?: PortalPasswordLocalization | null;
  validationLocalization?: FormsValidationLocalization | null;
}

export const PasswordInput: FC<PasswordInputProps> = ({
  defaultValue,
  name,
  handlePasswordChange,
  placeholder,
  localization,
  validationLocalization,
  ...props
}) => {
  const [passwordRequirements, setPasswordRequirements] = useState<
    PasswordRequirement[]
  >([]);
  const [showPassword, setShowPassword] = useState(false);

  const toggleVisibility = () => setShowPassword((prev) => !prev);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleValidation = useCallback(
    asyncFieldLevelMemo(async (value: string) => {
      const passwordReqs = await handlePasswordChange(value);
      setPasswordRequirements(passwordReqs);
      const incompletePasswordReqs = passwordReqs.some(
        (req) => req.satisfied === false
      );
      if (incompletePasswordReqs)
        return (
          validationLocalization?.invalidPasswordErrorText || "Invalid password"
        );
      return;
    }),
    []
  );

  return (
    <div>
      <Field
        name={name}
        {...props}
        defaultValue={defaultValue}
        validate={handleValidation}
        validateFields={[]}
      >
        {({ input, meta }) => (
          <StyledField>
            <StyledLabel>
              {props.label && <div>{props.label}</div>}
              <StyledPasswordContainer>
                <StyledInput
                  id={name}
                  type={showPassword ? "text" : "password"}
                  {...input}
                  {...props}
                  valid={meta.touched && !meta.error}
                  invalid={meta.touched && meta.error}
                  onChange={input.onChange}
                  placeholder={placeholder}
                  data-for="password-tooltip"
                  data-tip
                />
                <StyledEyeIconContainer onClick={toggleVisibility}>
                  {showPassword ? <EyeOpenIconSvg /> : <EyeClosedIconSvg />}
                </StyledEyeIconContainer>
              </StyledPasswordContainer>
            </StyledLabel>
            {meta.touched && meta.error && (
              <StyledError>{meta.error}</StyledError>
            )}
            {(meta.active || meta.touched) && (
              <StyledPasswordRequirementsContainer>
                <PasswordRequirements
                  passwordRequirements={passwordRequirements}
                  localization={localization}
                />
              </StyledPasswordRequirementsContainer>
            )}
          </StyledField>
        )}
      </Field>
    </div>
  );
};
